import React, { forwardRef } from 'react';
import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Input = forwardRef(({
  label,
  error,
  helperText,
  type = 'text',
  size = 'md',
  variant = 'default',
  leftIcon,
  rightIcon,
  placeholder,
  disabled = false,
  required = false,
  className = '',
  labelClassName = '',
  containerClassName = '',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const isPasswordType = type === 'password';
  const inputType = isPasswordType && showPassword ? 'text' : type;

  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  // Variant styles using CSS variables for proper theme support
  const variantStyles = {
    default: `
      border-input bg-background text-foreground
      focus:ring-ring focus:border-ring
      placeholder:text-muted-foreground
    `,
    filled: `
      border-input bg-muted text-foreground
      focus:ring-ring focus:border-ring focus:bg-background
      placeholder:text-muted-foreground
    `,
    ghost: `
      border-transparent bg-transparent text-foreground
      focus:ring-ring focus:border-input focus:bg-background
      placeholder:text-muted-foreground
    `
  };

  const baseInputStyles = `
    w-full rounded-lg border font-medium
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted
    ${error ? 'border-destructive focus:border-destructive focus:ring-destructive' : ''}
  `;

  const inputClasses = `
    ${baseInputStyles}
    ${sizeStyles[size]}
    ${variantStyles[variant]}
    ${leftIcon ? 'pl-10' : ''}
    ${rightIcon || isPasswordType ? 'pr-10' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const labelClasses = `
    block text-sm font-medium mb-2
    ${error ? 'text-destructive' : 'text-foreground'}
    ${disabled ? 'text-muted-foreground' : ''}
    ${labelClassName}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={containerClassName}>
      {label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {React.cloneElement(leftIcon, {
              className: `w-5 h-5 ${error ? 'text-destructive' : 'text-muted-foreground'}`
            })}
          </div>
        )}
        
        <input
          ref={ref}
          type={inputType}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          {...props}
        />
        
        {(rightIcon || isPasswordType) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {isPasswordType ? (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
                disabled={disabled}
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            ) : rightIcon ? (
              React.cloneElement(rightIcon, {
                className: `w-5 h-5 ${error ? 'text-destructive' : 'text-muted-foreground'}`
              })
            ) : null}
          </div>
        )}
        
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="w-5 h-5 text-destructive" />
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <div className="mt-2">
          {error && (
            <p className="text-sm text-destructive flex items-center">
              <ExclamationCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className="text-sm text-muted-foreground">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

// Search Input Component
export const SearchInput = ({ onSearch, placeholder = "Search...", className = '', value: controlledValue, ...props }) => {
  const [searchTerm, setSearchTerm] = React.useState(controlledValue || '');

  // Update internal state when controlled value changes
  React.useEffect(() => {
    if (controlledValue !== undefined) {
      setSearchTerm(controlledValue);
    }
  }, [controlledValue]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  return (
    <Input
      type="text"
      placeholder={placeholder}
      value={searchTerm}
      onChange={(e) => handleSearch(e.target.value)}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      role="search"
      name="search-query"
      leftIcon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      }
      className={className}
      {...props}
    />
  );
};

// Textarea Component
export const Textarea = forwardRef(({
  label,
  error,
  helperText,
  size = 'md',
  rows = 4,
  resize = 'vertical',
  disabled = false,
  required = false,
  className = '',
  labelClassName = '',
  containerClassName = '',
  ...props
}, ref) => {
  const sizeStyles = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const resizeStyles = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize'
  };

  const textareaClasses = `
    w-full rounded-lg border border-input bg-background text-foreground
    focus:ring-ring focus:border-ring
    placeholder:text-muted-foreground font-medium
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted
    ${error ? 'border-destructive focus:border-destructive focus:ring-destructive' : ''}
    ${sizeStyles[size]}
    ${resizeStyles[resize]}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const labelClasses = `
    block text-sm font-medium mb-2
    ${error ? 'text-destructive' : 'text-foreground'}
    ${disabled ? 'text-muted-foreground' : ''}
    ${labelClassName}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={containerClassName}>
      {label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={ref}
        rows={rows}
        className={textareaClasses}
        disabled={disabled}
        {...props}
      />
      
      {(error || helperText) && (
        <div className="mt-2">
          {error && (
            <p className="text-sm text-destructive flex items-center">
              <ExclamationCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className="text-sm text-muted-foreground">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';
Textarea.displayName = 'Textarea';

export default Input;